/**
 * League dynamics — model the rest of the league making believable trades.
 *
 * The Monte Carlo engine scores every team from its roster, so an opponent
 * "trade" is just a real roster change. We auto-generate dynasty-plausible deals
 * from per-team Buyer/Seller tags + a target deal count, then hand them to the
 * engine as ordinary tradeAdjustments (the same machinery your own trades use).
 *
 * Believability rules:
 *   • Sellers (rebuilders) ship AGING producers and RBs — not their young studs.
 *   • Buyers (contenders) pay a KTC-value-matched basket: draft picks + a young
 *     prospect. So the rebuilder gets picks AND youth, the contender gets win-now.
 *   • Pick values are tier-adjusted by the original team's projected finish, so a
 *     contender's own 1st is correctly a cheap late pick (they pay more of them).
 *   • Talent is conserved within the league; nothing is invented out of thin air.
 */

import { teamLineupMean } from './monteCarloEngine';

const STARTABLE = new Set(['QB', 'RB', 'WR', 'TE']);

// Small seeded PRNG so a given seed always reproduces the same market (frozen
// through a 100k run, reproducible when saved), but a new seed = a fresh market.
function mulberry32(a) {
  return function () {
    a |= 0; a = (a + 0x6D2B79F5) | 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

const pkKey = (p) => `${p.season}:${p.round}:${p.originalRosterId}`;
const ordinal = (n) => ['', '1st', '2nd', '3rd', '4th', '5th', '6th'][n] || `${n}th`;

/**
 * Default each opponent's trajectory from its baseline playoff odds:
 * top third → buyer, bottom third → seller, middle → pat. Your team is excluded.
 */
export function defaultLeagueTags(teams, baseResults, excludeRosterId) {
  const others = teams.filter((t) => t.rosterId !== excludeRosterId);
  const odds = (id) => baseResults?.teams?.[id]?.playoffProbability || 0;
  const ranked = [...others].sort((a, b) => odds(b.rosterId) - odds(a.rosterId));

  const n = ranked.length;
  const third = Math.max(1, Math.ceil(n / 3));
  const tags = {};
  ranked.forEach((t, i) => {
    if (i < third) tags[t.rosterId] = 'buyer';
    else if (i >= n - third) tags[t.rosterId] = 'seller';
    else tags[t.rosterId] = 'pat';
  });
  return tags;
}

// ─── Player / pick valuation ────────────────────────────────────────────────

// Effective trade value: KTC plus a super-flex starting-QB premium that KTC's
// raw number doesn't fully capture — QBs are scarce and sticky in SF, so they
// cost more to pry loose. No premium in 1QB (where QBs move more freely).
function effValue(p, vKey, isSuperFlex) {
  const v = p[vKey] || 0;
  if (p.position === 'QB') return Math.round(v * (isSuperFlex ? 1.25 : 1.0));
  return v;
}

// You can't sum KTC on a multi-asset package — everyone wants the premium asset,
// so a 2-for-1 / 3-for-1 has to overpay the raw total, more so with each piece.
function consolidationFactor(n) {
  return n <= 1 ? 1.0 : n === 2 ? 1.12 : n === 3 ? 1.22 : 1.32;
}

// How willing a rebuilder is to ship a player to a contender. Older + RB → more
// likely to be sold; young building blocks score near zero (they keep them).
// Startable QBs in SF are hoarded, so they rarely come up for sale at all.
function sellAppeal(p, isSuperFlex) {
  const exp = p.yearsExp == null ? 4 : p.yearsExp;
  const ageF = exp <= 1 ? 0.12 : exp === 2 ? 0.3 : exp <= 3 ? 0.7 : exp <= 5 ? 1.0 : 1.25;
  const posF = p.position === 'RB' ? 1.3
    : p.position === 'QB' ? (isSuperFlex ? 0.5 : 0.95)
    : 1.0;
  return (p.meanWeeklyPoints || 0) * ageF * posF;
}

// A young, valuable building block a rebuilder would NOT trade away.
// Young QBs are franchise cornerstones (held longest); other positions are kept
// through their early years if they carry real dynasty value.
function isYoungStud(p, vKey) {
  const exp = p.yearsExp == null ? 99 : p.yearsExp;
  const v = p[vKey] || 0;
  if (p.position === 'QB' && exp <= 3) return true;
  return (exp <= 3 && v >= 4000) || (exp <= 4 && v >= 6500);
}

// A young, lower-cost prospect a contender might include heading back to a rebuilder.
function isProspect(p, vKey) {
  const exp = p.yearsExp == null ? 99 : p.yearsExp;
  return exp <= 2 && (p[vKey] || 0) > 0;
}

// KTC-ish base value for a draft pick by round (super-flex scale).
function basePickValue(round) {
  return { 1: 6500, 2: 2600, 3: 1100, 4: 500 }[round] || 250;
}

// Where a pick is likely to land, from the original team's projected finish.
function pickTier(originalRosterId, results, numTeams) {
  const probs = results?.teams?.[originalRosterId]?.draftSlotProbs;
  if (!probs || !probs.length) return 'mid';
  let expSlot = 0;
  for (let i = 0; i < probs.length; i++) expSlot += i * probs[i];
  const frac = expSlot / Math.max(1, numTeams - 1); // 0 = pick 1.01, 1 = last
  return frac < 0.34 ? 'early' : frac > 0.66 ? 'late' : 'mid';
}

// Fallback only — a KTC-shaped curve for picks KTC doesn't list (2029+, 5th+).
function curvePickValue(pick, tier, currentSeason) {
  const tierMult = tier === 'early' ? 1.35 : tier === 'late' ? 0.72 : 1.0;
  const yearsOut = Math.max(0, (Number(pick.season) || currentSeason) - currentSeason);
  const future = Math.pow(0.85, yearsOut); // future picks are discounted
  return Math.round(basePickValue(pick.round) * tierMult * future);
}

// Real KTC value (season-round-tier → { sf, oneQB }) when available, else curve.
function pickValue(pick, tier, currentSeason, pickValues, pvKey) {
  const real = pickValues?.[`${pick.season}-${pick.round}-${tier}`]?.[pvKey];
  return real || curvePickValue(pick, tier, currentSeason);
}

/**
 * Assemble a buyer's return basket to ~match a target KTC value.
 * Prefers including one young prospect (rebuilders want youth) then fills with
 * draft picks. Returns null if the buyer can't get within ~80% of the target —
 * we'd rather skip a deal than print an unbelievable one.
 */
function buildPackage(targetEff, baseItems, baseValue, picks, prospects, used) {
  const items = [...baseItems];
  let sum = baseValue;

  // Sweeten toward the bar with a young prospect first (rebuilders want youth).
  const gap = targetEff * consolidationFactor(items.length + 1) - sum;
  if (gap > targetEff * 0.12) {
    const prospect = prospects.find((p) => !used.has(p.key) && p.value <= gap * 1.15 && p.value >= targetEff * 0.08);
    if (prospect) { items.push(prospect); sum += prospect.value; }
  }

  // Fill toward an escalating bar: each extra piece raises the premium the buyer
  // must pay (consolidationFactor), so a 3-for-1 has to clear well past raw KTC.
  const chosen = new Set(items.map((i) => i.key));
  const avail = picks.filter((p) => !used.has(p.key)).sort((a, b) => a.value - b.value);
  let guard = 0;
  while (guard++ < 12) {
    const bar = targetEff * consolidationFactor(items.length);
    if (items.length >= 1 && sum >= bar) break;        // premium met
    if (items.length >= 4) break;                       // no 7-for-1 junk piles
    const need = Math.max(0, targetEff * consolidationFactor(items.length + 1) - sum);
    let pk = [...avail].reverse().find((p) => !chosen.has(p.key) && p.value <= need * 1.3);
    if (!pk) pk = avail.find((p) => !chosen.has(p.key)); // smallest remaining
    if (!pk) break;
    items.push(pk);
    chosen.add(pk.key);
    sum += pk.value;
  }

  // Must actually pay the consolidation premium for the count of pieces, and not
  // grossly overpay (a spent buyer whose only pick left is a 1st, for a scrub).
  const bar = targetEff * consolidationFactor(items.length);
  if (items.length === 0 || sum < bar * 0.92 || sum > bar * 1.5) return null;
  for (const it of items) used.add(it.key);
  return { items, sum };
}

/**
 * Generate believable league trades from tags + a target deal count.
 *
 * @returns {{ trades: Array, summaries: Array }}
 */
export function generateLeagueTrades({
  teams,
  tags = {},
  targetTrades = 0,
  distributions = {},
  rosterPositions = [],
  results = null,
  isSuperFlex = true,
  currentSeason,
  pickValues = null,
  seed = 1,
  locked = [],
}) {
  if (!targetTrades || targetTrades <= 0) return { trades: [], summaries: [] };

  const vKey = isSuperFlex ? 'ktcValueSF' : 'ktcValue1QB';
  const pvKey = isSuperFlex ? 'sf' : 'oneQB';
  const numTeams = teams.length;
  const seasonNow = Number(currentSeason) || 2026;
  const rng = mulberry32((seed >>> 0) || 1);

  const buyers = teams.filter((t) => tags[t.rosterId] === 'buyer');
  const sellers = teams.filter((t) => tags[t.rosterId] === 'seller');
  if (!buyers.length || !sellers.length) return { trades: [], summaries: [] };

  const playersOf = (t) => (t.players || []).map((id) => ({ id, ...(distributions[id] || {}) })).filter((p) => p.position);

  // A market of sellable producers across all sellers, best win-now appeal first.
  const market = [];
  for (const s of sellers) {
    const chips = playersOf(s)
      .filter((p) => STARTABLE.has(p.position)
        && (p.meanWeeklyPoints || 0) > 0
        && !isYoungStud(p, vKey)
        && (p[vKey] || 0) > 600)
      .sort((a, b) => sellAppeal(b, isSuperFlex) - sellAppeal(a, isSuperFlex));
    for (const chip of chips) market.push({ seller: s, chip });
  }
  // Jitter the order so near-equal chips surface differently each seed — a fresh
  // but still-plausible market, not a reshuffle of who's good.
  for (const m of market) m._j = sellAppeal(m.chip, isSuperFlex) * (0.82 + 0.36 * rng());
  market.sort((a, b) => b._j - a._j);

  // Each buyer's spendable assets (picks valued by tier, plus young prospects).
  const buyerState = new Map();
  for (const b of buyers) {
    const picks = (b.picks || [])
      .map((pk) => {
        const tier = pickTier(pk.originalRosterId, results, numTeams);
        return { kind: 'pick', pick: pk, tier, value: pickValue(pk, tier, seasonNow, pickValues, pvKey), key: pkKey(pk) };
      })
      .sort((a, b2) => b2.value - a.value);
    const ps = playersOf(b);
    // Throw-in prospects only — never a young franchise QB or elite young stud
    // (a contender won't ship those as a sweetener).
    const prospects = ps
      .filter((p) => isProspect(p, vKey) && !isYoungStud(p, vKey))
      .map((p) => ({ kind: 'player', player: p, value: p[vKey] || 0, key: `pl:${p.id}` }))
      .sort((a, b2) => b2.value - a.value);
    // The buyer's best startable player at each position — what they'd upgrade FROM.
    const bestAt = {};
    for (const p of ps) {
      if (!STARTABLE.has(p.position)) continue;
      if (!bestAt[p.position] || (p[vKey] || 0) > (bestAt[p.position][vKey] || 0)) bestAt[p.position] = p;
    }
    buyerState.set(b.rosterId, { picks, prospects, bestAt, used: new Set() });
  }

  // Strongest contenders bid first — but with seeded jitter, so which contender
  // lands which player varies between markets while staying strength-weighted.
  const rankedBuyers = [...buyers]
    .map((b) => ({ b, s: teamLineupMean(b.players, distributions, rosterPositions) * (0.8 + 0.4 * rng()) }))
    .sort((x, y) => y.s - x.s)
    .map((x) => x.b);

  const trades = [];
  const summaries = [];
  const usedChips = new Set();

  // Pre-seed with locked trades: keep them in the market exactly as-is and mark
  // their players/picks used so a reroll never reassigns those assets.
  for (const lk of locked || []) {
    if (!lk || !lk.trade || !lk.summary) continue;
    trades.push(lk.trade);
    summaries.push(lk.summary);
    const g = lk.trade.giveAway && lk.trade.giveAway[0];
    if (!g) continue;
    usedChips.add(g.playerId);
    const st = buyerState.get(g.toRosterId);
    if (st) {
      for (const r of lk.trade.receive || []) st.used.add(`pl:${r.playerId}`);
      for (const pk of lk.trade.picksGiven || []) st.used.add(`${pk.season}:${pk.round}:${pk.originalRosterId}`);
    }
  }

  let bi = 0;

  for (const { seller, chip } of market) {
    if (trades.length >= targetTrades) break;
    if (usedChips.has(chip.id)) continue;

    const P = chip.position;
    const chipVal = chip[vKey] || 0;
    const targetEff = effValue(chip, vKey, isSuperFlex);

    // Find a contender who upgrades at this position by sending their worse
    // starter there + a sweetener (the dominant real-world trade shape).
    for (let k = 0; k < rankedBuyers.length; k++) {
      const buyer = rankedBuyers[(bi + k) % rankedBuyers.length];
      if (buyer.rosterId === seller.rosterId) continue;
      const st = buyerState.get(buyer.rosterId);

      // Upgrade-from: the buyer's best startable player at this position, clearly
      // worse than the chip (so the chip is a real upgrade) and not already moved.
      const gb = st.bestAt[P];
      const gbVal = gb ? (gb[vKey] || 0) : 0;
      // A real step up, not a premium-priced sidegrade: give-back is 30–85% of the chip.
      const canUpgrade = gb && !st.used.has(`pl:${gb.id}`) && gbVal < chipVal * 0.85 && gbVal > chipVal * 0.30;

      // QBs in super-flex only move as QB-for-QB upgrades — never for picks alone.
      if (P === 'QB' && isSuperFlex && !canUpgrade) continue;

      const baseItems = canUpgrade
        ? [{ kind: 'player', player: gb, value: effValue(gb, vKey, isSuperFlex), key: `pl:${gb.id}` }]
        : [];
      const baseValue = canUpgrade ? effValue(gb, vKey, isSuperFlex) : 0;

      const pkg = buildPackage(targetEff, baseItems, baseValue, st.picks, st.prospects, st.used);
      if (!pkg) continue;

      const receive = []; // players flowing buyer → seller (give-back + prospects)
      const picksGiven = []; // picks flowing buyer → seller
      const returnLabels = [];
      for (const it of pkg.items) {
        if (it.kind === 'player') {
          receive.push({ playerId: it.player.id, fromRosterId: buyer.rosterId, toRosterId: seller.rosterId });
          returnLabels.push(it.player.name || 'a player');
        } else {
          picksGiven.push({ ...it.pick, fromRosterId: buyer.rosterId, toRosterId: seller.rosterId });
          returnLabels.push(`${it.pick.season} ${ordinal(it.pick.round)}`);
        }
      }

      // Stable key (buyer + acquired player) so the user can X a deal and have it
      // stay removed across re-generations.
      const tradeKey = `${buyer.rosterId}:${chip.id}`;
      trades.push({
        key: tradeKey,
        giveAway: [{ playerId: chip.id, fromRosterId: seller.rosterId, toRosterId: buyer.rosterId }],
        receive,
        picksGiven,
        picksReceived: [],
      });
      summaries.push({
        key: tradeKey,
        buyerId: buyer.rosterId,
        buyerName: buyer.displayName,
        sellerId: seller.rosterId,
        sellerName: seller.displayName,
        getPlayer: chip.name || 'a player',
        position: P,
        returnLabels,
        value: chipVal,
        returnValue: Math.round(pkg.sum),
        upgrade: canUpgrade,
      });

      usedChips.add(chip.id);
      bi++;
      break;
    }
  }

  return { trades, summaries };
}
